import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './SolutionsModal.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMeteor,
  faInfoCircle,
  faNewspaper,
  faCheckCircle,
  faChartLine,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

// Traducciones del modal
const modalTranslations4 = {
  es: {
    header: 'Acompaña a tu equipo y corrige el rumbo si es necesario',
    intro:
      'Asegurando que cada talento avance de forma constante hacia sus metas. Tendrás un control total y podrás intervenir justo a tiempo para maximizar el desarrollo de tu gente.',
    sections: [
      {
        icon: faCheckCircle,
        title: 'Seguimiento de Hábitos con Dinámicas Gamificadas',
        bullets: [
          'Puntos y niveles: Cada micro-tarea completada suma puntos que se traducen en niveles de “maestría”.',
          'Retos y badges: Gana insignias al cumplir hitos (por ejemplo, “5 días seguidos de prácticas”) para impulsar la motivación.',
          'Clasificaciones internas: Fomenta la sana competencia mostrando tablas de progreso por equipo o departamento.',
        ],
      },
      {
        icon: faChartLine,
        title: 'Panel de Líder: Visión Integral',
        bullets: [
          'Dashboard unificado: Visualiza en un solo vistazo el estado de todos tus colaboradores: completado, en curso o detenido.',
          'Filtros inteligentes: Filtra por equipo, competencia o fase del plan para focalizarte en lo que más importa.',
          'Tendencias y benchmarks: Compara la evolución de tu equipo frente a objetivos internos y estándares de la industria.',
        ],
      },
      {
        icon: faBell,
        title: 'Alertas y Notificaciones',
        bullets: [
          'Alertas tempranas: Recibe avisos cuando un colaborador acumula 2 o más tareas retrasadas.',
          'Recordatorios proactivos: Notificaciones automáticas para animar a retomar actividades y mantener el ritmo.',
          'Sugerencias de intervención: La IA de Bether propone acciones concretas (mentoring, recursos extra) si detecta estancamiento.',
        ],
      },
    ],
    benefitsText:
      'Mira todos los beneficios que puedes obtener al poner en práctica una estrategia como esta:',
    sourceLabel: 'Fuente',
    kpis: [
      {
        metric: 'Calificación Media de Desempeño',
        baseline: 3.2,
        improved: 4.0,
        unit: '/5',
        center: '+25%',
        tooltipText:
          'Con BetherPlan® alineas objetivos SMART al perfil de cada empleado y calibras evaluaciones con datos robustos, lo que reduce la subjetividad y eleva la media anual de performance.',
        link: '',
      },
      {
        metric: 'Retención de “High Performers”',
        baseline: 81,
        improved: 93,
        unit: '%',
        center: '+12%',
        tooltipText:
          'Planes de carrera claros, mentoring focalizado y reconocimiento gamificado crean un entorno donde los mejores quieren quedarse, pasando del 81 % al 93 % de retención.',
        link: 'https://www.apollotechnical.com/employee-retention-statistics',
      },
      {
        metric: 'Tasa de Rotación Voluntaria',
        baseline: 17,
        improved: 11.7,
        unit: '%',
        center: '-31%',
        tooltipText:
          'Randstad sitúa el churn anual en una media del 17 % en España. Un programa de sucesión y retención efectivo (como el de Bether) lo reduce en un 31 %.',
        link: 'https://www.randstadresearch.es/tendencias-de-rrhh/',
      },
    ],
  },
  en: {
    header: 'Guide your team and correct course when necessary',
    intro:
      'Ensuring each talent advances steadily towards their goals. You’ll have complete control and be able to intervene just in time to maximize your people’s development.',
    sections: [
      {
        icon: faCheckCircle,
        title: 'Habit Tracking with Gamified Dynamics',
        bullets: [
          'Points & Levels: Each completed micro-task earns points that translate into mastery levels.',
          'Challenges & Badges: Earn badges for milestone achievements (e.g., "5 days of practice in a row") to boost motivation.',
          'Internal Leaderboards: Foster healthy competition by showing progress tables by team or department.',
        ],
      },
      {
        icon: faChartLine,
        title: 'Leader Panel: Holistic View',
        bullets: [
          'Unified Dashboard: View status of all your collaborators at a glance: completed, in progress, or stalled.',
          'Smart Filters: Filter by team, competency, or plan phase to focus on what matters most.',
          'Trends & Benchmarks: Compare your team’s evolution against internal goals and industry standards.',
        ],
      },
      {
        icon: faBell,
        title: 'Alerts & Notifications',
        bullets: [
          'Early Alerts: Receive warnings when an employee accumulates 2 or more overdue tasks.',
          'Proactive Reminders: Automatic notifications to encourage resuming activities and maintaining momentum.',
          'Intervention Suggestions: Bether’s AI proposes concrete actions (mentoring, extra resources) if stagnation is detected.',
        ],
      },
    ],
    benefitsText:
      'See all the benefits you can gain by implementing a strategy like this:',
    sourceLabel: 'Source',
    kpis: [
      {
        metric: 'Average Performance Rating',
        baseline: 3.2,
        improved: 4.0,
        unit: '/5',
        center: '+25%',
        tooltipText:
          'With BetherPlan® you align SMART objectives to each employee’s profile and calibrate evaluations with robust data, reducing subjectivity and raising the annual performance average.',
        link: '',
      },
      {
        metric: 'High Performer Retention',
        baseline: 81,
        improved: 93,
        unit: '%',
        center: '+12%',
        tooltipText:
          'Clear career plans, focused mentoring, and gamified recognition create an environment where top talent wants to stay, increasing retention from 81% to 93%.',
        link: 'https://www.apollotechnical.com/employee-retention-statistics',
      },
      {
        metric: 'Voluntary Turnover Rate',
        baseline: 17,
        improved: 11.7,
        unit: '%',
        center: '-31%',
        tooltipText:
          'Randstad places annual churn at an average of 17% in Spain. An effective succession and retention program (like Bether’s) reduces it by 31%.',
        link: 'https://www.randstadresearch.es/tendencias-de-rrhh/',
      },
    ],
  },
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0];
    return (
      <div className="tooltip-box">
        <strong>{entry.name}</strong>: {entry.value.toFixed(1)}
        {entry.payload.unit}
      </div>
    );
  }
  return null;
};

const KPICharts = ({ kpis, sourceLabel }) => {
  const [infoVisible, setInfoVisible] = useState({});
  const toggleInfo = (metric) =>
    setInfoVisible((prev) => ({ ...prev, [metric]: !prev[metric] }));

  return (
    <div className="smodal-tei-charts">
      {kpis.map(
        ({ metric, baseline, improved, unit, center, tooltipText, link }) => {
          // Si la unidad es /5, el máximo es 5; si no, 100.
          const maxValue = unit === '/5' ? 5 : 100;
          const outerData = [
            { name: 'Bether Average', value: improved, unit },
            { name: '', value: maxValue - improved },
          ];
          const innerData = [
            { name: 'Business Average', value: baseline, unit },
            { name: '', value: maxValue - baseline },
          ];

          return (
            <div key={metric} className="smodal-tei-chart-card">
              <h4 className="smodal-tei-chart-title">{metric}</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={outerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="65%"
                    outerRadius="95%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {outerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#ff2300' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={innerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="45%"
                    outerRadius="63%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {innerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#555555' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="smodal-tei-chart-center"
                  >
                    {center}
                  </text>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="info-icon"
                onClick={() => toggleInfo(metric)}
              />
              {infoVisible[metric] && (
                <div className="tooltip-popup">
                  <div className="tooltip-text">{tooltipText}</div>
                  {link && (
                    <button
                      className="source-button"
                      onClick={() => window.open(link, '_blank')}
                    >
                      <FontAwesomeIcon icon={faNewspaper} />
                      &nbsp;{sourceLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};

const SolutionsModal4 = ({ isOpen, onClose, language = 'es' }) => {
  if (!isOpen) return null;
  const t = modalTranslations4[language];

  return (
    <div className="smodal-overlay">
      <div className="smodal-content">
        <button className="smodal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="smodal-header">{t.header}</h2>
        <p className="smodal-tei-intro">{t.intro}</p>

        {t.sections.map((sec, idx) => (
          <div key={idx} className="smodal-section">
            <FontAwesomeIcon icon={sec.icon} className="section-icon" />
            <div className="section-content">
              <h3 className="section-title">{sec.title}</h3>
              {sec.bullets.length > 0 && (
                <ul className="section-list">
                  {sec.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}

        <div className="smodal-tei-box">
          <h3 className="smodal-tei-title">
            <FontAwesomeIcon icon={faMeteor} className="tei-icon" /> TEI: Total
            Economic Impact
          </h3>
          <p className="smodal-tei-intro">{t.benefitsText}</p>
          <KPICharts kpis={t.kpis} sourceLabel={t.sourceLabel} />
        </div>
      </div>
    </div>
  );
};

SolutionsModal4.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['es', 'en']),
};

export default SolutionsModal4;
